// #region Imports
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import { styled } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import React, { useMemo } from "react";
// #endregion

// #region Styled Components
const StyledAvatar = styled(Avatar)(({ theme }) => ({
  height: theme.spacing(10),
  width: theme.spacing(10),
}));
const LoadingProgress = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.grey[theme.palette.mode === "light" ? 200 : 700],
  height: "75%",
  width: "75%",
}));
// #endregion

// #region Types
export enum AvatarType {
  Individual,
  Company,
  Team,
}
// #endregion

// #region Component Props
export interface AvatarUploadProps {
  className?: string;
  accept: string;
  avatar?: File | string | null;
  onUpload: (updatedAvatar: File) => void;
}
// #endregion

// #region Component
export default function AvatarUpload(props: Readonly<AvatarUploadProps>) {
  const { className, accept, avatar, onUpload } = props;

  // #region Refs
  const hiddenAvatarFileInput = React.useRef<HTMLInputElement>(null);

  // #region State
  const [imageLoading, setImageLoading] = React.useState<boolean>(false);

  // #region Hooks

  const imageSrc = useMemo(() => {
    if (avatar instanceof File) return URL.createObjectURL(avatar);
    return avatar;
  }, [avatar]);

  // #region Functions
  function handleAvatarChange(event: React.ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;

    if (!files || files.length === 0) return;

    const avatarFile = files[0];

    try {
      onUpload(avatarFile);
    } catch (error) {
      console.error(error);
    } finally {
      hiddenAvatarFileInput.current!.value = "";
    }
  }
  // #endregion

  // #region Render
  return (
    <div className={className}>
      <input
        type="file"
        ref={hiddenAvatarFileInput}
        accept={accept}
        onChange={handleAvatarChange}
        style={{ display: "none" }}
      />
      <Badge
        overlap="rectangular"
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        badgeContent={
          <Tooltip title="Upload avatar">
            <span>
              <IconButton
                color="primary"
                aria-label="upload avatar"
                component="span"
                onClick={() => hiddenAvatarFileInput.current?.click()}
                size="medium"
              >
                {imageLoading ? <CircularProgress size="24px" disableShrink /> : <AddAPhotoIcon />}
              </IconButton>
            </span>
          </Tooltip>
        }
      >
        <StyledAvatar
          src={imageSrc ?? undefined}
          slotProps={{
            img: {
              onLoad: () => setImageLoading(false),
              onError: () => setImageLoading(false),
            },
          }}
        >
          {imageLoading && <LoadingProgress disableShrink />}
        </StyledAvatar>
      </Badge>
    </div>
  );
  // #endregion
}
